import { Formik, Form, useFormikContext } from "formik";
import * as React from "react";

import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import * as Yup from "yup";
import {
  formDataAtom,
  travelSearchAtom,
  travelReturnSearchAtom,
  selectedReturnJourneyAtom,
} from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import updateLocale from "dayjs/plugin/updateLocale";
import "dayjs/locale/sv";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import ScreenReaderAnnouncer, {
  getJourneySrLabel,
} from "../components/ScreenReaderAnnouncer";

import {
  ErrorList,
  Progressbar,
  Box,
  CustomAutocomplete,
  DatePicker,
  Button,
  Input,
  Label,
  RadioGroupForm,
  Icon,
  Alert,
  AlertTitle,
  Badge,
  cn,
  TrafficBadge,
  Heading,
} from "@ogt/ui";

export const ROUND_TRIP = "round_trip";
export const ONEWAY_TRIP = "one_way_trip";

interface Stop {
  PlaceName: string;
  OgtType: string;
  Ll: [number, number];
  Id: number;
}
interface Ogt {
  OgtTrafficTypeGroupCssClass: string;
  OgtTrafficTypeIconCssClass: string;
  OgtTrafficTypeIconReplaceLineName: boolean;
  OgtLineNamePreformatted: string;
}

interface Line {
  LineName: string;
  LineTypeId: number;
  LineTypeName: string;
  IsWalk: boolean;
  Ogt: Ogt;
}
interface RouteLink {
  Line: Line;
}
interface Journeys {
  Arrival: string;
  Departure: string;
  JourneyKey: string;
  TravelTime: string;
  Switch: string;
  RouteLinks: RouteLink[] | null;
  BookedGroupJourneys: number;
}

type TrafficMeta = Parameters<typeof TrafficBadge>[0]["meta"];

function toTrafficMeta(link: RouteLink): TrafficMeta {
  return {
    iconCss: link.Line.Ogt.OgtTrafficTypeIconCssClass,
    groupCss: link.Line.Ogt.OgtTrafficTypeGroupCssClass,
    title: link.Line.Ogt.OgtLineNamePreformatted ?? link.Line.LineName,
  };
}

function JourneySelectionWatcher({
  searchResults,
  onSelect,
}: {
  searchResults: Journeys[];
  onSelect: (value: string, result: Journeys) => void;
}) {
  const { values } = useFormikContext<any>();

  React.useEffect(() => {
    const key: string | undefined = values?.returnJourney;
    if (!key) return;
    const sel = searchResults.find((j) => j.JourneyKey === key);
    if (sel) onSelect(key, sel);
  }, [values?.returnJourney, searchResults, onSelect]);

  return null;
}

export default function TravelReturnSearch() {
  const apiBaseUrl =
    process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";

  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const progress = 32;

  const [formData, setFormData] = useAtom(formDataAtom);
  const [returnTravelSearch, setReturnTravelSearch] = useAtom(
    travelReturnSearchAtom
  );
  const [selectedReturnJourney, setSelectedReturnJourney] = useAtom(
    selectedReturnJourneyAtom
  );

  const [searchErrors, setSearchErrors] = useState({
    returnJourneyFromError: "",
    returnJourneyToError: "",
    returnJourneyTimeError: "",
    returnJourneyDateError: "",
    returnJourneyFormError: "",
    formError: "",
  });

  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [fromQuery, setFromQuery] = useState<string>("");
  const [toQuery, setToQuery] = useState<string>("");
  const [fromResults, setFromResults] = useState<Stop[]>([]);
  const [toResults, setToResults] = useState<Stop[]>([]);
  const [searchResults, setSearchResults] = useState<Journeys[]>([]);

  const [searchType, setSearchType] = useState<"from" | "to" | null>(null);

  const [debouncedFromQuery] = useDebounce(fromQuery, 300);
  const [debouncedToQuery] = useDebounce(toQuery, 300);
  const [travelSearch] = useAtom(travelSearchAtom);
  const [autocompleteAnnouncement, setAutocompleteAnnouncement] = useState("");

  dayjs.extend(localeData);
  dayjs.extend(updateLocale);

  dayjs.updateLocale("sv", {
    weekdays: [
      "Söndag",
      "Måndag",
      "Tisdag",
      "Onsdag",
      "Torsdag",
      "Fredag",
      "Lördag",
    ],
    months: [
      "januari",
      "februari",
      "mars",
      "april",
      "maj",
      "juni",
      "juli",
      "augusti",
      "september",
      "oktober",
      "november",
      "december",
    ],
  });

  useEffect(() => {
    if (!searchType) return;

    const results = searchType === "from" ? fromResults : toResults;

    if (!results) return;

    if (results.length > 0) {
      setAutocompleteAnnouncement(
        `Det finns ${results.length} sökförslag. Använd upp och nedpilar för att navigera.`
      );
    } else {
      setAutocompleteAnnouncement("");
    }

  }, [fromResults, toResults, searchType]);

  useEffect(() => {
    if (
      returnTravelSearch.selectedReturnJourneyFrom &&
      returnTravelSearch.selectedReturnJourneyTo &&
      returnTravelSearch.returnJourneyDate &&
      returnTravelSearch.returnJourneyTime
    ) {
      doTravelSearch(false, false);

      setFromQuery(returnTravelSearch.selectedReturnJourneyFrom.PlaceName);
      setToQuery(returnTravelSearch.selectedReturnJourneyTo.PlaceName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchType) return;

    const query = searchType === "from" ? debouncedFromQuery : debouncedToQuery;
    const trimmed = (query ?? "").trim();

    if (trimmed.length < 2) {
      if (searchType === "from") setFromResults([]);
      else setToResults([]);
      return;
    }

    const params = new URLSearchParams();
    params.set("q", trimmed); // don’t send pointType if API doesn’t like it

    fetch(`${apiBaseUrl}/api/v1/Points/Find?${params.toString()}`)
      .then(async (response) => {
        if (!response.ok) {
          if (searchType === "from") setFromResults([]);
          else setToResults([]);
          return null;
        }
        return response.json();
      })
      .then((data) => {
        if (!data) return;
        const arr: Stop[] = Array.isArray(data) ? data : [];
        if (searchType === "from") setFromResults(arr);
        else setToResults(arr);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        if (searchType === "from") setFromResults([]);
        else setToResults([]);
      });
  }, [debouncedFromQuery, debouncedToQuery, searchType, apiBaseUrl]);

  const searchFormRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleFocus = () => {
      if (searchFormRef.current) {
        const isActive = searchFormRef.current.contains(document.activeElement);
        setIsFocused(isActive);
      }
    };
    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleFocus);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleFocus);
    };
  }, []);

  const handleSelect = (placeName: string, type: "from" | "to") => {
    const list = type === "from" ? fromResults : toResults;
    const safeList: Stop[] = Array.isArray(list) ? list : [];
    const selectedItem =
      safeList.find((item) => item.PlaceName === placeName) || null;

    if (type === "from") {
      setReturnTravelSearch((prev) => ({
        ...prev,
        selectedReturnJourneyFrom: selectedItem,
      }));
      setFromQuery(selectedItem?.PlaceName || "");
      setFromResults([]);
    } else {
      setReturnTravelSearch((prev) => ({
        ...prev,
        selectedReturnJourneyTo: selectedItem,
      }));
      setToQuery(selectedItem?.PlaceName || "");
      setToResults([]);
    }
  };

  const handleQueryChange = (value: string, type: "from" | "to") => {
    if (type === "from") {
      setFromQuery(value);
      if (value.trim() === "") {
        setReturnTravelSearch((prev) => ({
          ...prev,
          selectedReturnJourneyFrom: null,
        }));
        setFromResults([]);
      }
    } else {
      setToQuery(value);
      if (value.trim() === "") {
        setReturnTravelSearch((prev) => ({
          ...prev,
          selectedReturnJourneyTo: null,
        }));
        setToResults([]);
      }
    }
    setSearchType(type);
  };

  const handleReturnTravelSearchChange = (
    field: keyof typeof returnTravelSearch,
    value: string
  ) => {
    setReturnTravelSearch((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      doTravelSearch(true);
    }
  };

  useEffect(() => {
    // Set value from first travelsearch as default value reversed
    if (
      travelSearch.selectedJourneyFrom?.PlaceName &&
      travelSearch.selectedJourneyTo?.PlaceName
    ) {
      setFromQuery(travelSearch.selectedJourneyTo.PlaceName);
      setToQuery(travelSearch.selectedJourneyFrom.PlaceName);

      setReturnTravelSearch((prev) => ({
        ...prev,
        selectedReturnJourneyFrom: travelSearch.selectedJourneyTo,
        selectedReturnJourneyTo: travelSearch.selectedJourneyFrom,
      }));
    }
  }, []);

  useEffect(() => {
    if (travelSearch.journeyDate && !returnTravelSearch.returnJourneyDate) {
      setReturnTravelSearch((prev) => ({
        ...prev,
        returnJourneyDate: travelSearch.journeyDate,
      }));
    }
  }, [
    travelSearch.journeyDate,
    returnTravelSearch.returnJourneyDate,
    setReturnTravelSearch,
  ]);

  const formatTimeInput = (value: string) => {
    const cleanedInput = value.replace(/\D/g, "");
    let newTime = "00:00";

    if (cleanedInput.length === 1) {
      newTime = `0${cleanedInput}:00`;
    } else if (cleanedInput.length === 2) {
      const hours = cleanedInput;
      newTime = `${hours === "24" ? "00" : hours}:00`;
    } else if (cleanedInput.length === 3) {
      const hours =
        cleanedInput[0] === "2" && cleanedInput[1] === "4"
          ? "00"
          : `0${cleanedInput[0]}`;
      const minutes = cleanedInput.slice(1);
      newTime = `${hours}:${minutes}`;
    } else if (cleanedInput.length >= 4) {
      let hours = cleanedInput.slice(0, 2);
      const minutes = cleanedInput.slice(2, 4);
      if (hours === "24") {
        hours = "00";
      }
      newTime = `${hours}:${minutes}`;
    }

    setReturnTravelSearch((prev) => ({
      ...prev,
      returnJourneyTime: newTime,
    }));

    return newTime;
  };

  const doTravelSearch = (
    clearSelectedReturnJourney: boolean = false,
    announce: boolean = true
  ) => {
    if (announce) {
      setHasSearched(true);
      setIsSearching(true);
    }
    const errors = {
      returnJourneyFromError: "",
      returnJourneyToError: "",
      returnJourneyDateError: "",
      returnJourneyTimeError: "",
    };

    if (
      !returnTravelSearch.selectedReturnJourneyFrom?.PlaceName.length ||
      returnTravelSearch.selectedReturnJourneyFrom.PlaceName.length < 1
    ) {
      errors.returnJourneyFromError = "Vänligen fyll i en avreseplats";
    }

    if (
      !returnTravelSearch.selectedReturnJourneyTo?.PlaceName.length ||
      returnTravelSearch.selectedReturnJourneyTo.PlaceName.length < 1
    ) {
      errors.returnJourneyToError = "Vänligen fyll i en destination";
    }

    if (!returnTravelSearch.returnJourneyDate.length) {
      errors.returnJourneyDateError = "Vänligen fyll i ett datum";
    } else {
      const today = dayjs().startOf("day");
      const minDate = today.add(5, "days").startOf("day");
      const selectedDate = dayjs(returnTravelSearch.returnJourneyDate);

      if (selectedDate.isBefore(minDate)) {
        errors.returnJourneyDateError =
          "Du kan endast välja ett datum som är minst 5 dagar framåt";
      }
    }

    if (returnTravelSearch.returnJourneyTime.length < 1) {
      errors.returnJourneyTimeError = "Vänligen fyll i en tid";
    }

    if (returnTravelSearch.returnJourneyDate && travelSearch.journeyDate) {
      const departureDate = dayjs(travelSearch.journeyDate);
      const returnDate = dayjs(returnTravelSearch.returnJourneyDate);

      if (returnDate.isBefore(departureDate)) {
        errors.returnJourneyDateError =
          "Returresan måste vara samma dag eller efter avresedagen.";
      }

      // Time comparison if same day
      if (returnDate.isSame(departureDate, "day")) {
        const departureTime = travelSearch.journeyTime;
        const returnTime = returnTravelSearch.returnJourneyTime;

        if (departureTime && returnTime) {
          const departure = dayjs(
            `${travelSearch.journeyDate}T${departureTime}`
          );
          const returning = dayjs(
            `${returnTravelSearch.returnJourneyDate}T${returnTime}`
          );

          if (returning.isBefore(departure)) {
            errors.returnJourneyTimeError =
              "Returresan måste ske efter utresan om den är samma dag.";
          }
        }
      }
    }

    //const today = dayjs().startOf("day");
    //const selectedDate = dayjs(returnTravelSearch.returnJourneyDate);

    setSearchErrors((prevErrors) => ({
      ...prevErrors,
      ...errors,
    }));

    const hasErrors = Object.values(errors).some(
      (error) => error.trim().length > 0
    );

    if (hasErrors) {
      setSearchResults([]);
      setIsSearching(false);
      setFormData((prev) => ({
        ...prev,
        returnJourney: "",
      }));
      return;
    }

    if (clearSelectedReturnJourney) {
      setSelectedReturnJourney({
        fromName: "",
        toName: "",
        date: "",
        travelTime: "",
        journeyKey: "",
        line: "",
        switches: "",
        unformattedDate: "",
      });

      setFormData((prev) => ({
        ...prev,
        returnJourney: "",
        //lectedReturnJourney,
      }));
    }

    const params = {
      startId: returnTravelSearch.selectedReturnJourneyFrom?.Id,
      endId: returnTravelSearch.selectedReturnJourneyTo?.Id,
      startType: returnTravelSearch.selectedReturnJourneyFrom?.OgtType,
      endType: returnTravelSearch.selectedReturnJourneyTo?.OgtType,
      startLl: `${returnTravelSearch.selectedReturnJourneyFrom?.Ll[0]},${returnTravelSearch.selectedReturnJourneyFrom?.Ll[1]}`,
      endLl: `${returnTravelSearch.selectedReturnJourneyTo?.Ll[0]},${returnTravelSearch.selectedReturnJourneyTo?.Ll[1]}`,
      startName: returnTravelSearch.selectedReturnJourneyFrom?.PlaceName,
      endName: returnTravelSearch.selectedReturnJourneyTo?.PlaceName,
      date: returnTravelSearch.returnJourneyDate,
      time: formatTimeInput(returnTravelSearch.returnJourneyTime),
      direction: 0,
      span: "Default",
      trafficTypes: 0,
      changetime: "Normal",
      priority: "ShortestTime",
      walk: false,
    };

    const searchParams = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, String(value)])
      )
    );

    fetch(
      `${apiBaseUrl}/api/v1/Journeys/FindForGroup?${searchParams.toString()}`
    )
      .then((response) => response.json())
      .then((data) => {
        // if (data.Journeys) {
        //     // Simulate a fully booked journey for testing
        //     data.Journeys[0].BookedGroupJourneys = 1; // Force the first journey to be fully booked
        // }

        let errorText = "";
        setSearchResults(
          (data.Journeys || []).map((j: Journeys) => ({
            ...j,
            BookedGroupJourneys: j.BookedGroupJourneys ?? 0,
          }))
        );
        if (
          data.ErrorText &&
          data.ErrorText.includes("returnJourneyDateTime")
        ) {
          errorText = "Vänligen ange en giltig tid";
        } else {
          errorText = data.ErrorText;
        }
        setSearchErrors({
          returnJourneyFromError: "",
          returnJourneyToError: "",
          returnJourneyTimeError: "",
          returnJourneyDateError: "",
          returnJourneyFormError: errorText || "",
          formError: "",
        });
        setIsSearching(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setIsSearching(false);
      });
  };

  const formatSelectedReturnJourney = (journey: any) => {
    return {
      fromName: journey.UsedStartPlace.PlaceName,
      toName: journey.UsedEndPlace.PlaceName,
      date: formatDate(journey.Departure, "dddd D MMMM"),
      travelTime: `${formatDate(journey.Departure, "HH:mm")} - ${formatDate(
        journey.Arrival,
        "HH:mm"
      )}`,
      journeyKey: journey.JourneyKey,
      line: journey.RouteLinks || [],
      switches: journey.Switch,
      unformattedDate: journey.Departure,
      UsedStartPlace: journey.UsedStartPlace,
      UsedEndPlace: journey.UsedEndPlace,
    };
  };

  const formatDate = (date: string, format: string) => {
    const formattedDate = dayjs(date).locale("sv").format(format);
    return formattedDate;
  };

  const groupResultsByDate = (results: Journeys[]) => {
    return results.reduce((acc: Record<string, Journeys[]>, result) => {
      const date = formatDate(result.Departure, "dddd D MMMM");
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(result);
      return acc;
    }, {});
  };

  const updateFormData = (value: string, result: any) => {
    setSelectedReturnJourney(formatSelectedReturnJourney(result));
  };

  const validationSchema = Yup.object().shape({
    returnJourney: Yup.string()
      .test(
        "at-least-one-radio-selected",
        "Vänligen välj en resa för att gå vidare till nästa steg",
        (value) => {
          return searchResults.some((result) => result.JourneyKey === value);
        }
      )
      .test(
        "not-fully-booked",
        "Resan är fullbokad, vänligen välj en annan resa",
        function (value) {
          const selectedJourney = searchResults.find(
            (journey) => journey.JourneyKey === value
          );

          if (!selectedJourney) return false; // No journey selected

          // Trigger error if BookedGroupJourneys is 0 (fully booked)
          return selectedJourney.BookedGroupJourneys === 0;
        }
      )
      .test(
        "return-after-departure",
        "Ange en senare tid för resan tillbaka. Den kan inte vara före resan dit.",
        function (value) {
          const context = this.options.context as any;

          const returnJourney = context?.searchResults?.find(
            (j: any) => j.JourneyKey === value
          );
          const selectedJourney = context?.formData?.selectedJourney;

          if (!returnJourney?.Departure || !selectedJourney?.unformattedArrival)
            return true;

          //   const arrivalTime = dayjs(selectedJourney.unformattedArrival);
          const arrivalTime = dayjs(
            context?.formData?.selectedJourney?.unformattedArrival
          );

          const returnTime = dayjs(returnJourney.Departure);

          return returnTime.isAfter(arrivalTime);
        }
      ),
  });

  return (
    <div className="travel-search-wrapper">
      <Formik<FormModel>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
            selectedReturnJourney,
            traveltype: ROUND_TRIP,
          });

          if (location.state?.fromSummary) {
            navigate(location.state.fromSummary);
          } else {
            navigate("/er-resa");
          }
        }}
        initialValues={{
          ...formData,
          returnJourney: formData.selectedReturnJourney?.journeyKey || "",
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validate={(values) => {
          try {
            validationSchema.validateSync(values, {
              context: {
                formData,
                selectedJourney: selectedReturnJourney, // används i din schema-test
                searchResults: searchResults,
              },
              abortEarly: false,
            });
            return {};
          } catch (err: any) {
            const errors: Record<string, string> = {};
            if (err.inner) {
              err.inner.forEach((validationError: Yup.ValidationError) => {
                if (validationError.path) {
                  errors[validationError.path] = validationError.message;
                }
              });
            } else if (err.path) {
              errors[err.path] = err.message;
            }
            return errors;
          }
        }}
      >
        {({ errors, values }) => {
          const politeAnnouncement = !hasSearched
            ? ""
            : isSearching
              ? "Söker resor."
              : searchErrors.returnJourneyFormError
                ? ""
                : searchResults.length === 0
                  ? "Inga resor hittades."
                  : `${searchResults.length} ${searchResults.length === 1 ? "resa hittades" : "resor hittades"
                  }. Välj en resa i listan nedan.`;

          const assertiveAnnouncement =
            searchErrors.returnJourneyFormError ||
            searchErrors.returnJourneyFromError ||
            searchErrors.returnJourneyToError ||
            searchErrors.returnJourneyDateError ||
            searchErrors.returnJourneyTimeError ||
            (errors?.returnJourney ? String(errors.returnJourney) : "");

          const combinedPoliteMessage = [politeAnnouncement, autocompleteAnnouncement]
            .filter(Boolean)
            .join(" ");

          return (
            <>
              <ScreenReaderAnnouncer
                politeMessage={combinedPoliteMessage}
                assertiveMessage={assertiveAnnouncement}
              />

              <div className="flex justify-end items-center">
                <div className="w-full max-w-xs mb-4">
                  <Progressbar percentage={progress} />
                </div>
              </div>

              <Form ref={formRef} noValidate>
                <JourneySelectionWatcher
                  searchResults={searchResults}
                  onSelect={(value, result) => updateFormData(value, result)}
                />

                <Box fullwidth={true}>
                  <Heading
                    progress={progress}
                    headingRef={h2Ref}
                    focusable
                    id="returnTravelSearchHeading"
                  >
                    Välj resa tillbaka
                  </Heading>

                  <p>
                    Fyll i hur ni vill resa tillbaka. Vill ni åka till en annan
                    hållplats än den ni reste från går det bra att ändra
                    hållplats.
                  </p>
                  <p className="mb-6">
                    Reser ni med Kulturbiljett? Tänk på att ni måste åka
                    tillbaka före klockan 18:00.
                  </p>

                  {searchErrors.returnJourneyFormError.length > 0 && (
                    <Alert showIcon variant="error">
                      <AlertTitle>
                        {searchErrors.returnJourneyFormError}
                      </AlertTitle>
                    </Alert>
                  )}

                  <div
                    className="travel-search-form grid gap-6 grid-cols-1 md:grid-cols-2"
                    ref={searchFormRef}
                  >
                    <CustomAutocomplete
                      id="returnJourneyFrom"
                      label="Från"
                      errorMessage={searchErrors.returnJourneyFromError}
                      value={
                        fromQuery !== undefined
                          ? fromQuery
                          : returnTravelSearch.selectedReturnJourneyFrom
                            ?.PlaceName || ""
                      }
                      onChange={(e: any) => {
                        setFromQuery(e.target.value);
                        setSearchType("from");
                      }}
                      onQueryChange={handleQueryChange}
                      options={fromResults}
                      onSelect={(placeName: any) =>
                        handleSelect(placeName, "from")
                      }
                      type="from"
                      onKeyDown={handleKeyDown}
                    />

                    <CustomAutocomplete
                      id="returnJourneyTo"
                      label="Till"
                      errorMessage={searchErrors.returnJourneyToError}
                      value={
                        toQuery !== undefined
                          ? toQuery
                          : returnTravelSearch.selectedReturnJourneyTo
                            ?.PlaceName || ""
                      }
                      onChange={(e: any) => {
                        setToQuery(e.target.value);
                        setSearchType("to");
                      }}
                      onQueryChange={handleQueryChange}
                      options={toResults}
                      onSelect={(placeName: any) =>
                        handleSelect(placeName, "to")
                      }
                      type="to"
                      onKeyDown={handleKeyDown}
                    />

                    <DatePicker
                      name="journey_date"
                      label={
                        <>
                          <span>Datum för resa</span>{" "}
                          <span>(Obligatoriskt)</span>
                        </>
                      }
                      value={
                        returnTravelSearch.returnJourneyDate ||
                        travelSearch.journeyDate
                      }
                      onChange={(value) =>
                        handleReturnTravelSearchChange(
                          "returnJourneyDate",
                          value
                        )
                      }
                      error={searchErrors.returnJourneyDateError}
                      maxDate={new Date().toISOString().split("T")[0]}
                      onKeyDown={handleKeyDown}
                      enableFromFiveDaysAhead
                    />

                    <div className="input-container gap-2 flex flex-col">
                      <Label htmlFor="journey_time">
                        <span>
                          Ungefärlig avgångstid <span>(Obligatoriskt)</span>
                        </span>
                      </Label>

                      <Input
                        type="text"
                        id="journey_time"
                        name="journey_time"
                        value={returnTravelSearch.returnJourneyTime || ""}
                        onChange={(e) =>
                          handleReturnTravelSearchChange(
                            "returnJourneyTime",
                            e.target.value
                          )
                        }
                        onKeyDown={handleKeyDown}
                      />

                      {searchErrors.returnJourneyTimeError.length > 0 && (
                        <Alert showIcon variant="error">
                          <AlertTitle>
                            {searchErrors.returnJourneyTimeError}
                          </AlertTitle>
                        </Alert>
                      )}
                    </div>
                  </div>

                  <Button
                    iconPosition="right"
                    aria-label="Sök resa"
                    className="mt-6"
                    size="medium"
                    variant="primary"
                    onClick={() => doTravelSearch(true)}
                    id="searchButton"
                    ref={searchButtonRef}
                  >
                    Sök resa
                  </Button>

                  {Object.keys(errors).length > 0 && (
                    <div className="mt-4">
                      <ErrorList errors={errors} />
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="search-results-container mt-6 flex flex-col gap-6">
                      {Object.entries(groupResultsByDate(searchResults)).map(
                        ([date, results]) => (
                          <div key={date}>
                            {(() => {
                              const uniqueResults = results.filter(
                                (item, index, self) =>
                                  index ===
                                  self.findIndex(
                                    (t) => t.JourneyKey === item.JourneyKey
                                  )
                              );

                              const makeLines = (result: Journeys) => {
                                if (
                                  !result.RouteLinks ||
                                  !Array.isArray(result.RouteLinks)
                                ) {
                                  return null;
                                }

                                return (
                                  <span className="lineicon-container flex-wrap inline-flex items-center gap-1">
                                    {result.RouteLinks.map((item, index) => {
                                      const meta = toTrafficMeta(item);
                                      const isPendeln =
                                        item.Line.LineName?.includes(
                                          "Östgötapendeln"
                                        );
                                      const showText =
                                        !item.Line.Ogt
                                          .OgtTrafficTypeIconReplaceLineName;

                                      return (
                                        <TrafficBadge
                                          key={index}
                                          meta={meta}
                                          className={cn(
                                            "lineicon",
                                            item.Line.LineTypeName,
                                            item.Line.Ogt
                                              .OgtTrafficTypeGroupCssClass,
                                            isPendeln && "pendeln"
                                          )}
                                          titleOverride={undefined}
                                        >
                                          {showText ? item.Line.LineName : null}
                                        </TrafficBadge>
                                      );
                                    })}
                                  </span>
                                );
                              };

                              const options = uniqueResults.map((result) => {
                                const isChecked =
                                  values.returnJourney === result.JourneyKey;
                                const isBooked =
                                  result.BookedGroupJourneys === 1;

                                return {
                                  id: result.JourneyKey,
                                  value: result.JourneyKey,
                                  disabled: isBooked,
                                  label: (
                                    <>
                                      <span className="sr-only">
                                        {getJourneySrLabel(result, formatDate, {
                                          isBooked: isBooked,
                                        })}
                                      </span>

                                      <div
                                        aria-hidden="true"
                                        className={[
                                          "flex flex-row w-full gap-x-6 gap-y-2 items-center flex-wrap [&>*]:leading-normal",
                                          isChecked ? "checked" : "",
                                          isBooked ? "fully-booked" : "",
                                        ]
                                          .filter(Boolean)
                                          .join(" ")}
                                      >
                                        {makeLines(result)}

                                        <span className="group-hover/radio-row:underline travel-departure-arrival inline-flex items-center">
                                          {formatDate(
                                            result.Departure,
                                            "HH:mm"
                                          )}{" "}
                                          –{" "}
                                          {formatDate(result.Arrival, "HH:mm")}
                                        </span>

                                        <span className="travel-time inline-flex items-center md:ml-auto min-w-[100px]">
                                          Restid: {result.TravelTime}
                                        </span>

                                        <span className="switch inline-flex items-center min-w-[60px]">
                                          {result.Switch}{" "}
                                          {parseInt(result.Switch, 10) === 1
                                            ? "byte"
                                            : "byten"}
                                        </span>

                                        {isBooked && (
                                          <Badge
                                            variant="red"
                                            shape="rounded"
                                            state="default"
                                            icons={
                                              <Icon
                                                name="alert-triangle"
                                                size={14}
                                              />
                                            }
                                          >
                                            Fullbokad
                                          </Badge>
                                        )}
                                      </div>
                                    </>
                                  ),
                                };
                              });

                              return (
                                <>
                                  {errors.returnJourney && (
                                    <Alert showIcon variant="error">
                                      <AlertTitle>
                                        {errors.returnJourney.toString()}
                                      </AlertTitle>
                                    </Alert>
                                  )}

                                  <Heading as="h3">
                                    {date}, {uniqueResults.length}{" "}
                                    {uniqueResults.length > 1
                                      ? "resor"
                                      : "resa"}
                                  </Heading>

                                  <p className="full-width">
                                    Sök resan i appen eller på webben för att se
                                    mer information om de olika avgångarna.
                                  </p>

                                  <RadioGroupForm
                                    name="returnJourney"
                                    legend="Välj en resa"
                                    label={null}
                                    description={null}
                                    orientation="vertical"
                                    variant="box"
                                    required
                                    underlineOnHover={false}
                                    className={cn(
                                      "radio-group !w-full mb-6",
                                      errors.returnJourney && "has-error"
                                    )}
                                    itemClassName=""
                                    options={options}
                                  />
                                </>
                              );
                            })()}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </Box>

                <div className="flex justify-center">
                  <div className="w-full text-left flex items-center">
                    <Button
                      iconPosition="left"
                      iconSize={16}
                      iconName="arrow-left"
                      aria-label="Tillbaka"
                      showIcon
                      size="medium"
                      variant="outline"
                      onClick={(event) => {
                        event.preventDefault();
                        const backUrl = undefined;
                        if (backUrl) {
                          navigate(backUrl, { replace: true });
                        } else {
                          navigate(-1);
                        }
                      }}
                    >
                      Tillbaka
                    </Button>
                  </div>

                  <div className="w-full text-right">
                    {searchResults.length > 0 && (
                      <Button
                        type="submit"
                        className="btn primary"
                        disabled={isFocused}
                      >
                        Gå vidare
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
}
